import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCheckoutSteps } from '@ajat/design-system/Checkout';
import { useCart } from '../../../../context/CartContext';

/**
 * Shared checkout hook for all brand checkout pages.
 *
 * The step / form / panel state is the package's `useCheckoutSteps` (`@ajat/design-system/Checkout` — the
 * 3-step shell OVER/LL builds on too; importing it also loads the shell's `Checkout.css`). This hook adds what is
 * the webshop's: the brand cart (`useCart`), the checkout call with its payment modes, the brand attribute.
 *
 * @param {Object} config
 * @param {string} config.brandSlug      - Cart brand slug ('clsdk' | 'abcse')
 * @param {string} config.brand          - Brand code for data-brand attr ('CLS' | 'ABC')
 * @param {string} config.priceSuffix    - Price suffix (',-' | ':-')
 * @param {string} config.thankYouRoute  - Navigate target after checkout (payment modes: `completed` → thank-you,
 *                                         `iframe` → in-page window, `redirect` → provider's hosted page, `failed` → error)
 * @param {Object|null} config.initialFormData - Initial form state ({} or null)
 * @param {Object} config.validators     - { fieldName: (value) => errorMsg | '' }
 * @param {number} config.editStep       - Step to return to when editing info
 */
/** Full-page navigation to a payment provider's hosted page (kept separate so tests can stub it). */
export const redirectTo = (url) => { window.location.assign(url); };

const useCheckout = ({
  brandSlug,
  brand,
  priceSuffix,
  thankYouRoute,
  initialFormData,
  validators,
  editStep,
  buildCheckoutPayload,
}) => {
  const navigate = useNavigate();
  const cart = useCart(brandSlug);
  const { total, checkout } = cart;

  const steps = useCheckoutSteps({ initialFormData, validators, editStep });
  const { formData, delivery, payment, validateAll, goToStep } = steps;

  /* ── Iframe payment state (Kustom) ── */
  const [iframePayment, setIframePayment] = useState(null);
  // { snippet, provider, reference, orderNumber }

  /* ── Checkout state ── */
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  /* ── Brand attribute ── */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', brand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [brand]);

  /* ── Checkout dispatch ── */
  const handleCheckout = useCallback(async () => {
    if (checkingOut) return;
    // Belt-and-braces: the info step already gates on validateAll, but the
    // final submit must never go out with an invalid form (e.g. a field
    // cleared after returning to edit). On failure, bounce back to the info
    // step with every error visible.
    if (!validateAll()) {
      goToStep(editStep, { scrollTop: true });
      return;
    }
    setCheckingOut(true);
    setCheckoutError('');
    let response;
    try {
      const payload = buildCheckoutPayload
        ? buildCheckoutPayload({ formData, delivery, payment })
        : {};
      response = await checkout(payload);
    } catch (err) {
      setCheckoutError(err?.message || 'Checkout failed');
      setCheckingOut(false);
      return;
    }

    const mode = response?.payment?.mode;
    const orderNumber = response?.orderNumber;

    if (mode === 'iframe') {
      setIframePayment({
        snippet:   response.payment.snippet,
        provider:  response.payment.provider,
        reference: response.payment.reference,
        orderNumber,
      });
      setCheckingOut(false);
      return;
    }

    if (mode === 'completed') {
      const url = `${thankYouRoute}?order=${encodeURIComponent(orderNumber)}&status=success`;
      navigate(url, { state: { order: response, orderNumber } });
      return;
    }

    // Hosted payment page (Visma Pay): leave the SPA for the provider's page; it sends
    // the customer back to the thank-you page, which confirms server-side. `checkingOut`
    // stays true so the button cannot start a second checkout while the browser navigates.
    if (mode === 'redirect') {
      const url = response?.payment?.url;
      if (typeof url === 'string' && /^https:\/\//i.test(url)) {
        redirectTo(url);
        return;
      }
      setCheckoutError(response?.payment?.message || 'Payment failed');
      setCheckingOut(false);
      return;
    }

    if (mode === 'failed') {
      setCheckoutError(response?.payment?.message || 'Payment failed');
      setCheckingOut(false);
      return;
    }

    // Fallback: navigate to thank-you with whatever we got.
    navigate(thankYouRoute, { state: { order: response, orderNumber } });
  }, [checkout, checkingOut, navigate, thankYouRoute, buildCheckoutPayload, formData, delivery, payment, validateAll, goToStep, editStep]);

  const cancelIframePayment = useCallback(() => {
    setIframePayment(null);
  }, []);

  /* ── Price display ── */
  const priceDisplay = `${Math.round(total)}${priceSuffix}`;

  return {
    ...steps,
    checkingOut,
    checkoutError,
    iframePayment,
    cancelIframePayment,
    priceDisplay,
    total,
    priceSuffix,
    handleCheckout,
  };
};

export default useCheckout;
